/**
 * REST API client for Story Management
 *
 * Uses axios directly (NOT baseRequest.ts) since klydo-api /stories endpoints:
 * - Have no Firebase authentication guard
 * - Are accessed directly by tester-server
 */

import axios from "axios";
import { baseUrl } from "api/baseRequest";
import type { TesterStory, StoryFormData } from "../types";

/**
 * Story Management API client
 */
export const storyManagementApi = {
  /**
   * Get all stories (including disabled)
   * Uses includeDisabled=true to show all stories for management
   */
  getAll(): Promise<TesterStory[]> {
    return axios
      .get(`${baseUrl}/stories?includeDisabled=true`)
      .then((res) => res.data);
  },

  /**
   * Create a new story
   * Automatically sets source to 'manual' for backoffice-created stories
   */
  create(data: StoryFormData): Promise<TesterStory> {
    return axios
      .post(`${baseUrl}/stories`, {
        ...data,
        source: "manual",
      })
      .then((res) => res.data);
  },

  /**
   * Update an existing story
   * Supports partial updates and toggling enabled status
   */
  update(
    id: string,
    data: Partial<StoryFormData> & { enabled?: boolean },
  ): Promise<TesterStory> {
    return axios.put(`${baseUrl}/stories/${id}`, data).then((res) => res.data);
  },

  /**
   * Delete a story
   * Returns confirmation of deletion
   */
  remove(id: string): Promise<{ deleted: boolean }> {
    return axios.delete(`${baseUrl}/stories/${id}`).then((res) => res.data);
  },
};
